import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useIntl } from 'react-intl';

import { useCallbackRef } from '../../../core/hooks/useCallbackRef.js';
import { useId } from '../../../core/hooks/useId.js';
import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import { getI18nValidationState } from '../../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../../forms-core/state/formfield-messages-state-reducer.js';
import type { FormControlRef } from '../../shared-types.js';
import {
  type MessageTarget,
  normalizeMessageTarget,
} from '../../validation/message-routing.js';
import type { ValidationState } from '../../validation/types.js';
import { useCustomMessageEmitter } from '../../validation/useCustomMessageEmitter.js';
import type { ValidateFieldOptions } from '../form.types.js';
import { useFormRuntimeRegistration } from './useFormRuntimeRegistration.js';

export interface InternalFormRegistrationOptions<TValue = unknown> {
  id?: string;
  name?: string;
  form?: string;
  /**
   * Where this control's native/Strato messages render, from the control's
   * `_messageTarget` prop. Defaults to the field outlet.
   */
  messageTarget?: MessageTarget | MessageTarget[];
  value?: TValue;
  controlRef?: RefObject<FormControlRef<HTMLElement> | null>;
  onValidityChange?: (state: ValidationState) => void;
  getValue?: () => unknown;
  focus?: () => void;
  validateField?: (options: ValidateFieldOptions) => Promise<ValidationState>;
  resetValidation?: () => void;
}

/**
 * Internal registration hook for Strato-owned controls. Strato controls compute their
 * own `ValidationState` (native + Strato + custom messages) through the
 * `FormField` message pipeline; this hook hands that pipeline's
 * `validateField` and value/focus accessors to the enclosing `<Form>` so the
 * control participates in submit, aggregation, snapshot, and focus-first-invalid
 * with no per-control runtime of its own.
 *
 * It is the counterpart to the public `useFormRegistration`: same
 * registration primitive, but fed by the Strato validity pipeline instead of a
 * consumer-supplied validator. Returns a `reportValidity` the control tees its
 * live emissions into, or a no-op when there is no enclosing form.
 * @internal
 */
export function useInternalFormRegistration<TValue = unknown>(
  options: InternalFormRegistrationOptions<TValue>,
): { id: string; onValidityChange: (state: ValidationState) => void } {
  const {
    id,
    name,
    form,
    messageTarget,
    value,
    controlRef,
    onValidityChange,
    getValue,
    focus,
    validateField,
    resetValidation,
  } = options;
  const intl = useIntl();
  const generatedId = useId();
  const formFieldContext = useFormFieldContext();
  const { dispatch } = useFormFieldMessagesContext();

  // Publish the control's routing choice into the field context so the two
  // read-points (the `FormFieldMessages` render filter and the
  // `useCustomMessageEmitter` emit path) resolve it without per-control logic.
  const normalizedTarget =
    messageTarget === undefined
      ? undefined
      : normalizeMessageTarget(messageTarget);
  const messageTargetKey = normalizedTarget?.join(',') ?? '';
  const normalizedTargetRef = useRef(normalizedTarget);
  normalizedTargetRef.current = normalizedTarget;
  useEffect(() => {
    dispatch?.({
      type: FormFieldMessagesAction.SETMESSAGETARGET,
      payload: { messageTarget: normalizedTargetRef.current },
    });
  }, [dispatch, messageTargetKey]);
  const suppressLiveValidityReportRef = useRef(false);
  const reportValidityRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);

  // Consumers routinely pass a fresh `onValidityChange` on every render (e.g. an
  // inline arrow). Read it through a stable ref so `handleValidityChange` — and
  // the `useCustomMessageEmitter` re-emit effects that depend on it — keep a
  // stable identity; otherwise every emitted state re-triggers those effects,
  // and when the consumer's handler also sets state it becomes an infinite loop.
  const onValidityChangeStable = useCallbackRef(onValidityChange);
  const handleValidityChange = useCallback(
    (state: ValidationState) => {
      onValidityChangeStable(state);
      if (!suppressLiveValidityReportRef.current) {
        reportValidityRef.current?.(state);
      }
    },
    [onValidityChangeStable],
  );
  const emitWithCustomMessages = useCustomMessageEmitter(
    handleValidityChange,
    () => getI18nValidationState(intl, controlRef?.current?.inputRef, 'manual'),
  );

  const defaultValidateField = ({ reveal, trigger }: ValidateFieldOptions) => {
    controlRef?.current?.inputRef?.checkValidity();
    if (reveal) {
      dispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });
    }
    return emitWithCustomMessages(
      getI18nValidationState(intl, controlRef?.current?.inputRef, trigger),
    );
  };

  const resolvedId =
    id ??
    formFieldContext.formFieldId ??
    // DEPRECATION: APPDEV-18319 - Remove deprecated `controlId`.
    // oxlint-disable-next-line typescript/no-deprecated
    formFieldContext.controlId ??
    generatedId;

  const wrappedValidateField = useCallback(
    async (validateOptions: ValidateFieldOptions) => {
      suppressLiveValidityReportRef.current = true;
      try {
        return await (validateField ?? defaultValidateField)(validateOptions);
      } finally {
        suppressLiveValidityReportRef.current = false;
      }
    },
    [defaultValidateField, validateField],
  );

  const reportValidity = useFormRuntimeRegistration({
    id: resolvedId,
    name,
    form,
    getValue: getValue ?? (() => value),
    focus: focus ?? (() => controlRef?.current?.focus?.()),
    validateField: wrappedValidateField,
    resetValidation:
      resetValidation ??
      (() => {
        controlRef?.current?.inputRef?.setCustomValidity('');
        dispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: false, resetShowError: true },
        });
      }),
  });

  reportValidityRef.current = reportValidity;

  return { id: resolvedId, onValidityChange: handleValidityChange };
}
